/**
 * Il caricamento del piano MES — il pezzo che mancava a M2.
 *
 * Fino a oggi l'unico gesto disponibile era «Reimporta piano da MES», che
 * rilegge due `.xls` **congelati dentro l'immagine** (l'export di aprile,
 * committato a giugno): un pulsante che promette un aggiornamento impossibile.
 * È la ragione per cui il piano in produzione è rimasto al 29 aprile mentre la
 * pagina lo mostrava come «piano corrente».
 *
 * Servono i **due** export insieme, perché il piano è uno: l'estrusione da sola
 * darebbe un piano senza confezionamento (e senza il legame PF↔semilavorato, che
 * oggi arriva proprio dal piano di confezionamento).
 */
import { AlertTriangle, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useM2CaricaPiano } from "@/hooks/useM2";
import { cn } from "@/lib/utils";

const ESTENSIONI = ".xls,.xlsx";

function Slot({
  etichetta,
  aiuto,
  file,
  onFile,
}: {
  etichetta: string;
  aiuto: string;
  file: File | null;
  onFile: (f: File | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [sopra, setSopra] = useState(false);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        // Senza dichiarare il dropEffect il browser rifiuta il rilascio in
        // silenzio — lezione del calendario, #39.
        e.dataTransfer.dropEffect = "copy";
        setSopra(true);
      }}
      onDragLeave={() => setSopra(false)}
      onDrop={(e) => {
        e.preventDefault();
        setSopra(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      className={cn(
        "rounded-lg border border-dashed p-3 transition-colors",
        sopra ? "border-brand-600 bg-brand-50 dark:bg-brand-950/30" : "bg-muted/40"
      )}
    >
      <div className="text-[12.5px] font-medium">{etichetta}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{aiuto}</div>
      {file ? (
        <div className="mt-2 flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5">
          <FileSpreadsheet className="size-3.5 shrink-0 text-brand-700" />
          <span className="min-w-0 flex-1 truncate text-[12px]">{file.name}</span>
          <button
            type="button"
            onClick={() => onFile(null)}
            className="text-muted-foreground hover:text-foreground"
            aria-label={`Togli ${file.name}`}
          >
            <X className="size-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="mt-2 w-full rounded-md border bg-card px-3 py-1.5 text-[12px] font-medium hover:bg-accent/40"
        >
          Scegli il file o trascinalo qui
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept={ESTENSIONI}
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}

export function CaricaPianoDialog({
  aperto,
  onChiudi,
}: {
  aperto: boolean;
  onChiudi: () => void;
}) {
  const [estr, setEstr] = useState<File | null>(null);
  const [conf, setConf] = useState<File | null>(null);
  const carica = useM2CaricaPiano();
  const pronto = Boolean(estr && conf) && !carica.isPending;

  function chiudi() {
    setEstr(null);
    setConf(null);
    carica.reset();
    onChiudi();
  }

  return (
    <Dialog open={aperto} onOpenChange={(v) => !v && chiudi()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Carica il piano dal MES</DialogTitle>
          <DialogDescription>
            I due export del MES, quelli a 17 colonne. Sostituiscono il piano corrente; il
            precedente non resta (non ne teniamo lo storico).
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2.5">
          <Slot
            etichetta="Piano di estrusione"
            aiuto="Colonna «Materiale» = codice del semilavorato"
            file={estr}
            onFile={setEstr}
          />
          <Slot
            etichetta="Piano di confezionamento"
            aiuto="Colonna «1° semilavorato» = il legame prodotto↔semilavorato"
            file={conf}
            onFile={setConf}
          />
        </div>

        {carica.isError && (
          <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[12.5px] text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
            <AlertTriangle className="size-4 shrink-0" />
            <span>{carica.error instanceof Error ? carica.error.message : "Caricamento fallito."}</span>
          </div>
        )}

        {carica.isSuccess ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[12.5px] text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300">
            Piano caricato: <b>{carica.data.kpi.lavorazioni_estrusione}</b> lavorazioni di
            estrusione e <b>{carica.data.kpi.lavorazioni_confezionamento}</b> di confezionamento su{" "}
            <b>{carica.data.kpi.giorni_con_lavorazioni}</b> giornate.
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            Le <b>date di disponibilità</b> verso Commesse e Spedizioni non vengono ricalcolate da
            qui: è un gesto a parte, perché sposta numeri che vedono altri moduli.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={chiudi}
            className="rounded-md border bg-card px-3.5 py-2 text-sm font-medium hover:bg-accent/40"
          >
            {carica.isSuccess ? "Chiudi" : "Annulla"}
          </button>
          {!carica.isSuccess && (
            <button
              type="button"
              disabled={!pronto}
              onClick={() => estr && conf && carica.mutate({ estrusione: estr, confezionamento: conf })}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {carica.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Upload className="size-3.5" />
              )}
              Carica il piano
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
